import React, { memo, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

// helpers
import { log } from 'shared/helpers/newRelic';
import { TAG_IDENTIFIER } from 'shared/helpers/newRelic/constants';
import { isWebView } from 'shared/helpers/utils/url';
import { useEligibleSSRVariantName } from 'shared/components/ExperimentV2/helpers';
import {
  navigateToURL,
  shouldShowAppMigrationTopBanner,
  getAppMigrationOneLinkUrl,
  isIOSDevice,
} from 'shared/components/AppMigration/helpers';
import {
  trackAppMigrationClick,
  buildAppMigrationInteractionLocation,
  getAppMigrationAnalyticsPage,
} from 'shared/components/AppMigration/analytics';

// hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// store
import { getWebAppType } from 'shared/store/app/selectors';
import { getIsAppMigrationTopBannerDismissed } from 'shared/store/appMigration/selectors';
import { dismissAppMigrationTopBanner } from 'shared/store/appMigration/actions';

// constants
import {
  APP_MIGRATION_EXP,
  APP_MIGRATION_EXP_VARIANT_A,
  STORE_URLS,
} from 'shared/components/AppMigration/constants';

// styles
import {
  BannerContainer,
  LeftSection,
  AppIconContainer,
  TextContent,
  BannerText,
  RightSection,
  CTAButton,
  CloseButton,
} from './styles';

// icons
import { FashionLogoSmall, CloseCircleIcon } from 'shared/components/AppMigration/icons';

// types
import { IAppMigrationBannerProps } from 'shared/components/AppMigration/types';

function AppMigrationTopBanner({
  className,
  onClose,
  onCTAClick,
  onVisible,
}: IAppMigrationBannerProps) {
  const dispatch = useDispatch();
  const location = useLocation();
  const { pathname, search } = location;

  const webAppType = useSelector(getWebAppType);
  const isTopBannerDismissed = useSelector(getIsAppMigrationTopBannerDismissed);
  const appMigrationEnabled = !!useRemoteConfig('appMigrationEnabled');
  const isTopBannerVisible = !!useRemoteConfig('appMigrationTopBannerVisible');
  const appMigrationContent = useRemoteConfig('appMigrationContent');
  const appsFlyerLinks = useRemoteConfig('appMigrationAppsFlyerLinks');
  const topBannerConfig = appMigrationContent?.topBanner;
  const bannerText = topBannerConfig?.text ?? 'Install now to get exciting app-only offers';
  const ctaText = topBannerConfig?.ctaText ?? 'Download App Now!';

  const { isEligible = false, variant = '' } = useEligibleSSRVariantName(APP_MIGRATION_EXP);
  const isAppMigrationVariantA = isEligible && variant === APP_MIGRATION_EXP_VARIANT_A;

  const isVisible = shouldShowAppMigrationTopBanner({
    isTopBannerDismissed,
    webAppType,
    appMigrationEnabled,
    isTopBannerVisible,
    isAppMigrationVariantA,
    pathname,
    search,
    isWebView: isWebView(location),
  });

  useEffect(() => {
    if (isVisible && onVisible) {
      onVisible();
    }
  }, [isVisible, onVisible]);

  const handleCTAClick = useCallback(() => {
    trackAppMigrationClick({
      interactionLocation: buildAppMigrationInteractionLocation(
        getAppMigrationAnalyticsPage(),
        'banner'
      ),
    });

    const oneLinkUrl = getAppMigrationOneLinkUrl(appsFlyerLinks, 'topBanner');
    const url = oneLinkUrl || (isIOSDevice() ? STORE_URLS.APP_STORE : STORE_URLS.PLAY_STORE);

    if (!oneLinkUrl) {
      log({
        errTitle: '[AppMigration] Missing topBanner OneLink in remote config, using store fallback',
        tags: { [TAG_IDENTIFIER.MODULE]: 'AppMigrationTopBanner' },
      });
    }

    if (onCTAClick) {
      onCTAClick(url);
    }

    navigateToURL(url);
  }, [appsFlyerLinks, onCTAClick]);

  const handleClose = useCallback(() => {
    dispatch(dismissAppMigrationTopBanner());

    if (onClose) {
      onClose();
    }
  }, [dispatch, onClose]);

  if (!isVisible) {
    return null;
  }

  return (
    <BannerContainer className={className} data-testid="app-migration-top-banner">
      <LeftSection>
        <AppIconContainer>
          <FashionLogoSmall />
        </AppIconContainer>

        <TextContent>
          <BannerText>{bannerText}</BannerText>
        </TextContent>
      </LeftSection>

      <RightSection>
        <CTAButton onClick={handleCTAClick} data-testid="app-migration-top-banner-cta">
          {ctaText}
        </CTAButton>

        <CloseButton
          onClick={handleClose}
          aria-label="Close banner"
          data-testid="app-migration-top-banner-close"
        >
          <CloseCircleIcon />
        </CloseButton>
      </RightSection>
    </BannerContainer>
  );
}

export default memo(AppMigrationTopBanner);
